import React from 'react';
import { RefreshCw, Users } from 'lucide-react';

const BUCKET_TONE = {
  LOYAL_ON_TIME: 'loyal',
  DUE_SOON: 'due',
  AT_RISK: 'risk',
  SLIPPING_AWAY: 'risk',
  DORMANT: 'dormant',
  FIRST_TIME: 'new',
  HIGH_SPEND: 'vip',
  OFFER_SENSITIVE: 'coupon',
  ACTIVE: 'active'
};

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value || 0));

const formatCurrencyFromPaise = (paise) => {
  const rupees = Math.round(Number(paise || 0) / 100);
  return `Rs ${formatNumber(rupees)}`;
};

const formatLastVisit = (value) => {
  if (!value) {
    return 'No visit';
  }
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short'
  }).format(date);
};

const customerLabel = (customer) => (
  customer?.name || customer?.phone || `Customer ${customer?.customerKey || ''}`.trim()
);

const RetentionBucketsPanel = ({
  canUseRetentionBuckets,
  hasLoadedCapabilities,
  summary,
  customers,
  selectedBucket,
  onSelectBucket,
  onRefresh,
  loading,
  customersLoading,
  error
}) => {
  if (hasLoadedCapabilities && !canUseRetentionBuckets) {
    return (
      <section className="offers-retention-panel" aria-label="Customer retention">
        <div className="offers-retention-empty">
          <Users size={18} />
          <div>
            <h3>Customer retention</h3>
            <p>Retention buckets are not available for this store.</p>
          </div>
        </div>
      </section>
    );
  }

  const buckets = Array.isArray(summary?.buckets) ? summary.buckets : [];
  const selectedMeta = buckets.find((bucket) => bucket.bucket === selectedBucket);
  const customerRows = Array.isArray(customers?.customers) ? customers.customers : [];
  const totalCustomers = customers?.totalCustomers ?? customerRows.length;

  return (
    <section className="offers-retention-panel" aria-label="Customer retention">
      <div className="offers-retention-head">
        <div>
          <p className="offers-eyebrow">Customer retention</p>
          <h3>Customer buckets</h3>
        </div>
        <div className="offers-retention-head-actions">
          {summary?.expectedReturnCycleDays ? (
            <span className="offers-retention-cycle">
              {summary.expectedReturnCycleDays} day cycle
            </span>
          ) : null}
          <button
            className="btn ghost small offers-icon-btn"
            type="button"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh retention buckets"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {error && !summary ? (
        <div className="offers-retention-empty">
          <Users size={18} />
          <div>
            <h3>Retention data unavailable</h3>
            <p>Try refreshing this section.</p>
          </div>
        </div>
      ) : null}

      {!error && loading && !summary ? (
        <div className="offers-retention-grid" aria-label="Loading retention buckets">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="offers-retention-bucket skeleton" key={`retention-loading-${index}`} />
          ))}
        </div>
      ) : null}

      {buckets.length > 0 ? (
        <>
          <div className="offers-retention-grid">
            {buckets.map((bucket) => {
              const selected = bucket.bucket === selectedBucket;
              const tone = BUCKET_TONE[bucket.bucket] || 'active';
              return (
                <button
                  key={bucket.bucket}
                  type="button"
                  className={`offers-retention-bucket ${tone} ${selected ? 'selected' : ''}`}
                  onClick={() => onSelectBucket?.(bucket.bucket)}
                >
                  <span>{bucket.displayName || bucket.bucket}</span>
                  <strong>{formatNumber(bucket.count)}</strong>
                  <small>{bucket.suggestedAction}</small>
                </button>
              );
            })}
          </div>

          <div className="offers-retention-detail">
            <div className="offers-retention-detail-head">
              <div>
                <h4>{selectedMeta?.displayName || customers?.displayName || selectedBucket}</h4>
                <p>{selectedMeta?.suggestedAction || customers?.suggestedAction || 'Create a targeted campaign.'}</p>
              </div>
            </div>

            {customersLoading ? (
              <div className="offers-retention-customers-loading">Loading customers</div>
            ) : customerRows.length === 0 ? (
              <div className="offers-retention-customers-empty">
                No customers in this bucket.
              </div>
            ) : (
              <div className="offers-retention-customers">
                <div className="offers-retention-customer-row header">
                  <span>Customer</span>
                  <span>Last visit</span>
                  <span>Visits</span>
                  <span>Spend</span>
                </div>
                {customerRows.slice(0, 6).map((customer) => (
                  <div className="offers-retention-customer-row" key={customer.customerKey || customer.userUuid}>
                    <span>
                      <strong>{customerLabel(customer)}</strong>
                      <small>{customer.daysSinceLastVisit} days since visit</small>
                    </span>
                    <span>{formatLastVisit(customer.lastVisitDate)}</span>
                    <span>{formatNumber(customer.visitCount)}</span>
                    <span>{formatCurrencyFromPaise(customer.lifetimeSpendPaise)}</span>
                  </div>
                ))}
                {totalCustomers > customerRows.slice(0, 6).length ? (
                  <p className="offers-retention-more">
                    {formatNumber(totalCustomers - customerRows.slice(0, 6).length)} more customers
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default RetentionBucketsPanel;
